import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Award, Plus, Pencil, Trash2, Search, Medal, Filter } from "lucide-react";
import AdminDeleteConfirmation from "@/components/AdminDeleteConfirmation";

const MODALITIES = ["Clássico", "Rápido", "Blitz", "Bala", "Online", "Outro"];
const emptyForm = {
  athlete_id: "", competition_name: "", date: "",
  location: "", category: "", modality: "Clássico",
  rounds: 0, score: 0, placement: 0, medal: "",
  performance: "", coach_notes: "",
};

export default function AdminCompetitions() {
  const [athletes, setAthletes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAthlete, setFilterAthlete] = useState("__all__");
  const [q, setQ] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ath, comps] = await Promise.all([
        api.get("/athletes"),
        api.get("/competitions"),
      ]);
      setAthletes(ath.data);
      setItems(comps.data);
    } catch (err) {
      console.error("[competitions] load:", err);
      toast.error("Falha ao carregar dados");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  const athById = useMemo(() => {
    const m = {}; for (const a of athletes) m[a.id] = a; return m;
  }, [athletes]);

  const filtered = useMemo(() => {
    let arr = items;
    if (filterAthlete !== "__all__") arr = arr.filter(c => c.athlete_id === filterAthlete);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter(c =>
        (c.competition_name||"").toLowerCase().includes(s) ||
        (c.location||"").toLowerCase().includes(s) ||
        (c.category||"").toLowerCase().includes(s) ||
        (athById[c.athlete_id]?.name||"").toLowerCase().includes(s)
      );
    }
    return arr;
  }, [items, filterAthlete, q, athById]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, athlete_id: filterAthlete !== "__all__" ? filterAthlete : "" });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      athlete_id: c.athlete_id || "", competition_name: c.competition_name || "",
      date: c.date || "", location: c.location || "", category: c.category || "",
      modality: c.modality || "Clássico",
      rounds: c.rounds || 0, score: c.score || 0, placement: c.placement || 0,
      medal: c.medal || "", performance: c.performance || "",
      coach_notes: c.coach_notes || "",
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.athlete_id) { toast.error("Selecione o atleta"); return; }
    if (!form.competition_name || !form.date) { toast.error("Preencha nome da competição e data"); return; }
    setSaving(true);
    const payload = {
      ...form,
      rounds: Number(form.rounds) || 0,
      score: Number(form.score) || 0,
      placement: Number(form.placement) || 0,
    };
    try {
      if (editingId) {
        const { data } = await api.put(`/competitions/${editingId}`, payload);
        setItems(prev => prev.map(c => c.id === editingId ? data : c));
        toast.success("Resultado atualizado");
      } else {
        const { data } = await api.post("/competitions", payload);
        setItems(prev => [data, ...prev]);
        toast.success("Resultado cadastrado");
      }
      setDialogOpen(false); setEditingId(null); setForm(emptyForm);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/competitions/${c.id}`);
      setItems(prev => prev.filter(x => x.id !== c.id));
      toast.success("Resultado removido");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao remover");
    }
  };

  return (
    <AppLayout kind="admin">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold flex items-center gap-3">
            <Award className="w-8 h-8 text-amber-400" />
            Desempenho em Competições
          </h1>
          <p className="text-slate-400 mt-1">Registre manualmente resultados e desempenhos que alimentam o Ranking Interno GSX.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar…"
              data-testid="comp-search"
              className="pl-9 bg-[#0D2A52] border-amber-500/20 text-white w-56" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-amber-400" />
            <Select value={filterAthlete} onValueChange={setFilterAthlete}>
              <SelectTrigger data-testid="comp-athlete-filter" className="bg-[#0D2A52] border-amber-500/20 text-white w-56">
                <SelectValue placeholder="Todos os atletas" />
              </SelectTrigger>
              <SelectContent className="bg-[#071A33] text-white border-amber-500/20 max-h-80">
                <SelectItem value="__all__">Todos os atletas</SelectItem>
                {athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o)=>{setDialogOpen(o); if (!o) setEditingId(null);}}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="comp-new-btn"
                className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Novo Resultado
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar Resultado" : "Novo Resultado"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="grid sm:grid-cols-2 gap-4" data-testid="comp-form">
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-slate-300">Atleta *</Label>
                  <Select value={form.athlete_id} onValueChange={(v)=>setF("athlete_id", v)}>
                    <SelectTrigger data-testid="comp-form-athlete" className="bg-[#0D2A52] border-amber-500/20 text-white">
                      <SelectValue placeholder="Selecione o atleta" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#071A33] text-white border-amber-500/20 max-h-80">
                      {athletes.map(a => <SelectItem key={a.id} value={a.id}>{a.name} — {a.category || "—"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <F label="Nome da competição *" v={form.competition_name} set={(v)=>setF("competition_name", v)} testid="comp-form-name" required />
                <F label="Data *" type="date" v={form.date} set={(v)=>setF("date", v)} testid="comp-form-date" required />
                <F label="Local" v={form.location} set={(v)=>setF("location", v)} testid="comp-form-location" />
                <F label="Categoria" v={form.category} set={(v)=>setF("category", v)} testid="comp-form-category" />
                <div className="space-y-2">
                  <Label className="text-slate-300">Modalidade</Label>
                  <Select value={form.modality} onValueChange={(v)=>setF("modality", v)}>
                    <SelectTrigger data-testid="comp-form-modality" className="bg-[#0D2A52] border-amber-500/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#071A33] text-white border-amber-500/20">
                      {MODALITIES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <F label="Rodadas / participantes" type="number" v={form.rounds} set={(v)=>setF("rounds", v)} testid="comp-form-rounds" />
                <F label="Pontuação obtida" type="number" v={form.score} set={(v)=>setF("score", v)} testid="comp-form-score" step="0.5" />
                <F label="Colocação" type="number" v={form.placement} set={(v)=>setF("placement", v)} testid="comp-form-placement" />
                <F label="Medalha (Ouro/Prata/Bronze)" v={form.medal} set={(v)=>setF("medal", v)} testid="comp-form-medal" />
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-slate-300">Desempenho</Label>
                  <Textarea rows={2} value={form.performance}
                    onChange={(e)=>setF("performance", e.target.value)}
                    data-testid="comp-form-performance"
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-slate-300">Observações do treinador</Label>
                  <Textarea rows={2} value={form.coach_notes}
                    onChange={(e)=>setF("coach_notes", e.target.value)}
                    data-testid="comp-form-notes"
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                </div>
                <Button type="submit" disabled={saving} data-testid="comp-form-save"
                  className="sm:col-span-2 bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold h-11">
                  {saving ? "Salvando…" : (editingId ? "Salvar Alterações" : "Cadastrar Resultado")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading && <div className="text-slate-400 py-10 text-center">Carregando…</div>}
      {!loading && !filtered.length && (
        <div className="text-slate-400 py-10 text-center border border-dashed border-white/10 rounded-lg">
          {items.length ? "Nenhum resultado corresponde aos filtros." : "Nenhum resultado cadastrado ainda. Clique em Novo Resultado."}
        </div>
      )}

      {!loading && !!filtered.length && (
        <Card className="bg-transparent gsx-card-dark overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0D2A52] border-b border-amber-500/20 text-amber-300 text-xs uppercase tracking-widest">
                <tr>
                  <th className="p-3 text-left">Data</th>
                  <th className="p-3 text-left">Atleta</th>
                  <th className="p-3 text-left">Competição</th>
                  <th className="p-3 text-center">Modal.</th>
                  <th className="p-3 text-center">Col.</th>
                  <th className="p-3 text-center">Score</th>
                  <th className="p-3 text-center">Medalha</th>
                  <th className="p-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`comp-row-${c.id}`}>
                    <td className="p-3 font-mono text-slate-200">{c.date || "—"}</td>
                    <td className="p-3 text-white">{athById[c.athlete_id]?.name || <span className="text-slate-500">(atleta removido)</span>}</td>
                    <td className="p-3">
                      <div className="text-white">{c.competition_name}</div>
                      <div className="text-xs text-slate-400">{c.location || ""} {c.category ? `• ${c.category}` : ""}</div>
                    </td>
                    <td className="p-3 text-center text-xs text-slate-300">{c.modality || "—"}</td>
                    <td className="p-3 text-center font-mono text-amber-300">{c.placement ? `${c.placement}º` : "—"}</td>
                    <td className="p-3 text-center font-mono text-white">{c.score || 0}</td>
                    <td className="p-3 text-center">
                      {c.medal ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-amber-500/15 border border-amber-500/40 text-amber-300 px-2 py-0.5 rounded">
                          <Medal className="w-3 h-3" /> {c.medal}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button size="icon" variant="ghost" onClick={()=>openEdit(c)}
                          data-testid={`comp-edit-${c.id}`}
                          className="border border-amber-400/70 bg-amber-500/15 text-amber-200 hover:bg-amber-400 hover:text-[#071A33] hover:border-amber-300 h-8 w-8">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <AdminDeleteConfirmation itemName={`${c.competition_name} — ${athById[c.athlete_id]?.name || "atleta"}`} onConfirm={() => remove(c)}><Button size="icon" variant="ghost"
                          data-testid={`comp-delete-${c.id}`}
                          className="text-red-400 hover:bg-red-500/10 h-8 w-8">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button></AdminDeleteConfirmation>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#0D2A52]/50 text-xs">
                <tr>
                  <td colSpan={8} className="p-3 text-slate-400">
                    <span className="text-amber-300 font-semibold">{filtered.length}</span> resultado(s)
                    {filterAthlete !== "__all__" && ` de ${athById[filterAthlete]?.name || ""}`}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </AppLayout>
  );
}

function F({ label, v, set, type = "text", required, testid, step }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input type={type} value={v ?? ""} required={required} data-testid={testid} step={step}
        onChange={(e)=>set(e.target.value)}
        className="bg-[#0D2A52] border-amber-500/20 text-white" />
    </div>
  );
}
